import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Box, Button, Typography, TextField } from "@mui/material";
import { FaDownload } from "react-icons/fa";
import SaveButton from "components/buttonsComponent/SaveButton";
import DownloadButton from "components/buttonsComponent/DownloadButton";
import UserIdButton from "components/buttonsComponent/UserIdButton";
import ReportCountdownButton from "components/buttonsComponent/ReportCountdownButton";

// ============================================
// CONSTANTS
// ============================================
const FORM_WIDTH = 1200;
const TABLE_MIN_WIDTH = "900px";
const THEME = {
  primary: "#02462E",
  secondary: "#0E186B",
  lightGray: "#f9f9f9",
  darkGray: "#666",
  border: "#ccc",
  danger: "red",
};

const FIELD_STYLES = {
  text: {
    borderBottom: `1px solid ${THEME.primary}`,
    fontFamily: "DM Sans",
    fontSize: "14px",
    paddingBottom: "2px",
  },
  textarea: {
    "& .MuiOutlinedInput-root": {
      fontFamily: "DM Sans",
      fontSize: "14px",
    },
  },
  label: {
    fontFamily: "DM Sans",
    fontSize: "14px",
  },
};

const TABLE_STYLES = {
  table: {
    width: "100%",
    minWidth: TABLE_MIN_WIDTH,
    borderCollapse: "collapse",
    fontFamily: "DM Sans",
    fontSize: "12px",
    border: `1px solid ${THEME.border}`,
  },
  headerCell: {
    border: `1px solid ${THEME.border}`,
    padding: "10px",
    textAlign: "left",
    fontWeight: "bold",
    minWidth: "80px",
  },
  bodyCell: {
    border: `1px solid ${THEME.border}`,
    padding: "10px",
    minWidth: "80px",
  },
};

// ============================================
// HELPER FUNCTIONS
// ============================================
const getFieldKey = (sectionIndex, fieldIndex) =>
  `section_${sectionIndex}_field_${fieldIndex}`;

const getTableKey = (sectionIndex) => `section_${sectionIndex}`;

const isOtherOption = (option) =>
  option?.toLowerCase().includes("other") ||
  option?.toLowerCase().includes("specify");

const cleanOptionLabel = (option) =>
  option?.split("(specify")[0]?.split("(please specify")[0]?.trim() || "";

// ============================================
// MAIN COMPONENT
// ============================================
const SmartTemplate = ({
  title = "Download MoM Template",
  subtitle = "Meeting Minutes & Action Tracking",
  formTitle = "MoM Template",
  sections = [],
  extraBottomActions = [],           // ← NEW: Support for Add Next button
  downloadFileName = "mom_template",
  downloadFileExtension = "png",
  formImageUrl = null,
  onNext,
  onPrevious,
  onSave,
  nextButtonText = "Next",
  showSaveButton = true,
  showDownloadButton = true,
  showNextButton = true,
  showPreviousButton = false,
  titleColor = "#02462E",
  subtitleColor = "#02462E",
  showReportCountdownButton = false,
  downloadButtonTitle = "Download Form",
}) => {
  const [isDownloaded, setIsDownloaded] = useState(false);
  const [formData, setFormData] = useState({});
  const [tableRows, setTableRows] = useState({});
  const [filePreviews, setFilePreviews] = useState({});

  // ============================================
  // INITIALIZATION
  // ============================================
  useEffect(() => {
    const initialFormData = {};
    const initialTableRows = {};

    sections.forEach((section, sectionIndex) => {
      if (section.type === "table") {
        const key = getTableKey(sectionIndex);
        initialTableRows[key] = Array(section.defaultRows || 2)
          .fill(null)
          .map(() => ({}));
      }

      section.fields?.forEach((field, fieldIndex) => {
        if (field.preSelected && field.type === "checkbox") {
          const key = getFieldKey(sectionIndex, fieldIndex);
          initialFormData[key] = [...field.options];
        }
      });
    });

    if (Object.keys(initialFormData).length > 0) {
      setFormData((prev) => ({ ...prev, ...initialFormData }));
    }
    if (Object.keys(initialTableRows).length > 0) {
      setTableRows(initialTableRows);
    }
  }, [sections]);

  // ============================================
  // EVENT HANDLERS
  // ============================================
  const handleNext = useCallback(() => {
    onNext?.({ formData, tableRows, filePreviews });
  }, [onNext, formData, tableRows, filePreviews]);

  const handleAddAnother = useCallback(() => {
    onNext?.({ formData, tableRows, filePreviews }, { isAddAnother: true });
  }, [onNext, formData, tableRows, filePreviews]);

  const handlePrevious = useCallback(() => {
    onPrevious?.();
  }, [onPrevious]);

  const handleSave = useCallback(() => {
    onSave?.({ formData, tableRows, filePreviews });
  }, [onSave, formData, tableRows, filePreviews]);

  const handleDownloadClick = useCallback(() => {
    if (!formImageUrl) return;

    const link = document.createElement("a");
    link.href = formImageUrl;
    link.download = `${downloadFileName}.${downloadFileExtension}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setIsDownloaded(true);
    setTimeout(() => setIsDownloaded(false), 2000);
  }, [formImageUrl, downloadFileName, downloadFileExtension]);

  const handleInputChange = useCallback((key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleCheckboxChange = useCallback(
    (sectionIndex, fieldIndex, checked, optionValue) => {
      const key = getFieldKey(sectionIndex, fieldIndex);
      setFormData((prev) => {
        const currentVal = prev[key] || [];
        if (checked) {
          return { ...prev, [key]: [...currentVal, optionValue] };
        }
        return {
          ...prev,
          [key]: currentVal.filter(
            (v) => v !== optionValue && !v.startsWith("Other:")
          ),
        };
      });
    },
    []
  );

  const handleTableRowChange = useCallback(
    (sectionIndex, rowIndex, fieldIndex, value) => {
      const key = getTableKey(sectionIndex);
      setTableRows((prev) => {
        const currentRows = [...(prev[key] || [])];
        currentRows[rowIndex] = {
          ...currentRows[rowIndex],
          [`field_${fieldIndex}`]: value,
        };
        return { ...prev, [key]: currentRows };
      });
    },
    []
  );

  const handleAddTableRow = useCallback((sectionIndex) => {
    const key = getTableKey(sectionIndex);
    setTableRows((prev) => ({
      ...prev,
      [key]: [...(prev[key] || []), {}],
    }));
  }, []);

  const handleRemoveTableRow = useCallback((sectionIndex, rowIndex) => {
    const key = getTableKey(sectionIndex);
    setTableRows((prev) => ({
      ...prev,
      [key]: (prev[key] || []).filter((_, i) => i !== rowIndex),
    }));
  }, []);

  // ============================================
  // RENDER FIELD COMPONENTS
  // ============================================
  const renderTextInput = useCallback(
    (field, key) => (
      <TextField
        fullWidth
        type={field.type}
        variant="standard"
        value={formData[key] || ""}
        onChange={(e) => handleInputChange(key, e.target.value)}
        placeholder={field.placeholder}
        disabled={field.disabled}
        InputProps={{ disableUnderline: false, sx: FIELD_STYLES.text }}
      />
    ),
    [formData, handleInputChange]
  );

  const renderTextarea = useCallback(
    (field, key) => (
      <TextField
        fullWidth
        multiline
        rows={field.rows || 3}
        variant="outlined"
        value={formData[key] || ""}
        onChange={(e) => handleInputChange(key, e.target.value)}
        placeholder={field.placeholder}
        disabled={field.disabled}
        sx={FIELD_STYLES.textarea}
      />
    ),
    [formData, handleInputChange]
  );

  const renderFileInput = useCallback(
    (field, key) => {
      const currentFiles = filePreviews[key] || [];

      return (
        <Box>
          <input
            type="file"
            accept={field.accept}
            multiple={field.multiple || false}
            onChange={(e) => {
              const files = e.target.files;
              if (!files || files.length === 0) return;

              const fileNames = Array.from(files).map((f) => f.name);
              setFilePreviews((prev) => ({ ...prev, [key]: fileNames }));
              handleInputChange(key, files); // Save FileList object
            }}
            style={{
              fontSize: "14px",
              fontFamily: "DM Sans",
              padding: "8px 0",
              display: "block",
            }}
          />
          {currentFiles.length > 0 && (
            <Box sx={{ mt: 1, fontSize: "13px", color: "#2e7d32" }}>
              Selected: {currentFiles.join(", ")}
            </Box>
          )}
        </Box>
      );
    },
    [filePreviews, handleInputChange]
  );

  const renderCheckbox = useCallback(
    (field, key, sectionIndex, fieldIndex) => (
      <Box>
        {field.options?.map((option, optionIndex) => {
          const checkedValues = formData[key] || [];
          const isOther = isOtherOption(option);
          const isChecked = isOther
            ? checkedValues.some((v) => v.startsWith("Other:"))
            : checkedValues.includes(option);

          return (
            <Box
              key={optionIndex}
              display="flex"
              alignItems="center"
              mb={1}
              sx={{ gap: 1 }}
            >
              <input
                type="checkbox"
                id={`${key}_${optionIndex}`}
                checked={isChecked}
                disabled={field.disabled}
                onChange={(e) => {
                  if (!field.disabled) {
                    handleCheckboxChange(
                      sectionIndex,
                      fieldIndex,
                      e.target.checked,
                      option
                    );
                  }
                }}
                style={{
                  marginRight: 8,
                  cursor: field.disabled ? "not-allowed" : "pointer",
                }}
              />
              <label
                htmlFor={`${key}_${optionIndex}`}
                style={{ ...FIELD_STYLES.label, flex: 1 }}
              >
                {cleanOptionLabel(option)}
              </label>
            </Box>
          );
        })}
      </Box>
    ),
    [formData, handleCheckboxChange]
  );

  const renderRadio = useCallback(
    (field, key) => (
      <Box>
        {field.options?.map((option, idx) => (
          <Box key={idx} display="flex" alignItems="center" mb={1}>
            <input
              type="radio"
              name={key}
              id={`${key}_${idx}`}
              checked={formData[key] === option}
              onChange={() => handleInputChange(key, option)}
              style={{ marginRight: 8 }}
            />
            <label htmlFor={`${key}_${idx}`} style={FIELD_STYLES.label}>
              {option}
            </label>
          </Box>
        ))}
      </Box>
    ),
    [formData, handleInputChange]
  );

  const renderTableField = useCallback(
    (field, rowValue, sectionIndex, rowIndex, fieldIndex) => {
      const value = rowValue?.[`field_${fieldIndex}`] || "";
      const tableKey = `${getTableKey(sectionIndex)}_row_${rowIndex}_field_${fieldIndex}`;
      const commonProps = {
        fullWidth: true,
        size: "small",
        value,
        onChange: (e) =>
          handleTableRowChange(
            sectionIndex,
            rowIndex,
            fieldIndex,
            e.target.value
          ),
      };

      if (field.type === "text") {
        return (
          <TextField
            {...commonProps}
            type="text"
            variant="standard"
            placeholder={field.placeholder}
            InputProps={{
              disableUnderline: false,
              sx: { fontSize: "12px", fontFamily: "DM Sans" },
            }}
          />
        );
      }
      if (field.type === "file") {
        const currentFiles = filePreviews[tableKey] || [];
        return (
          <Box>
            <input
              type="file"
              accept={field.accept}
              multiple={field.multiple}
              onChange={(e) => {
                const files = e.target.files;
                const fileNames = Array.from(files).map(f => f.name);
                setFilePreviews(prev => ({ ...prev, [tableKey]: fileNames }));
                handleTableRowChange(sectionIndex, rowIndex, fieldIndex, files);
              }}
              style={{ fontSize: "11px" }}
            />
            {currentFiles.length > 0 && (
              <Box sx={{ fontSize: "10px", color: "green", mt: 0.5 }}>
                {currentFiles.join(", ")}
              </Box>
            )}
          </Box>
        );
      }

      if (field.type === "date") {
        return (
          <TextField
            {...commonProps}
            type="date"
            variant="standard"
            InputProps={{ disableUnderline: false, sx: { fontSize: "12px" } }}
          />
        );
      }

      if (field.type === "textarea") {
        return (
          <TextField
            {...commonProps}
            multiline
            rows={2}
            variant="outlined"
            placeholder={field.placeholder}
            sx={{ fontSize: "12px", fontFamily: "DM Sans" }}
          />
        );
      }

      if (field.type === "checkbox") {
        return (
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {field.options?.map((option, optIdx) => (
              <Box key={optIdx} display="flex" alignItems="center">
                <input
                  type="checkbox"
                  id={`table_${sectionIndex}_${rowIndex}_${fieldIndex}_${optIdx}`}
                  checked={(value || []).includes(option)}
                  onChange={(e) => {
                    const currentVal = value || [];
                    const newVal = e.target.checked
                      ? [...currentVal, option]
                      : currentVal.filter((v) => v !== option);
                    handleTableRowChange(
                      sectionIndex,
                      rowIndex,
                      fieldIndex,
                      newVal
                    );
                  }}
                  style={{ marginRight: 4, cursor: "pointer" }}
                />
                <label
                  htmlFor={`table_${sectionIndex}_${rowIndex}_${fieldIndex}_${optIdx}`}
                  style={{ fontSize: "12px", cursor: "pointer" }}
                >
                  {option}
                </label>
              </Box>
            ))}
          </Box>
        );
      }

      return null;
    },
    [handleTableRowChange, filePreviews]
  );

  const renderField = useCallback(
    (field, sectionIndex, fieldIndex) => {
      const key = getFieldKey(sectionIndex, fieldIndex);

      const fieldTypeMap = {
        text: () => renderTextInput(field, key),
        email: () => renderTextInput(field, key),
        date: () => renderTextInput(field, key),
        "datetime-local": () => renderTextInput(field, key),
        textarea: () => renderTextarea(field, key),
        checkbox: () => renderCheckbox(field, key, sectionIndex, fieldIndex),
        radio: () => renderRadio(field, key),
        file: () => renderFileInput(field, key),
      };

      return fieldTypeMap[field.type]?.() || null;
    },
    [renderTextInput, renderTextarea, renderCheckbox, renderRadio, renderFileInput]
  );

  const renderTable = useCallback(
    (section, sectionIndex) => {
      const rows = tableRows[getTableKey(sectionIndex)] || [];

      return (
        <Box sx={{ overflowX: "auto", mb: 3 }}>
          <table style={TABLE_STYLES.table}>
            <thead>
              <tr style={{ backgroundColor: "#f5f5f5" }}>
                {section.fields?.map((field, idx) => (
                  <th
                    key={idx}
                    style={{
                      ...TABLE_STYLES.headerCell,
                      width: field.width || "auto",
                    }}
                  >
                    {field.label}
                  </th>
                ))}
                <th
                  style={{
                    ...TABLE_STYLES.headerCell,
                    width: "70px",
                    textAlign: "center",
                  }}
                >
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  style={{
                    backgroundColor: rowIndex % 2 === 0 ? "#fff" : "#fafafa",
                  }}
                >
                  {section.fields?.map((field, fieldIndex) => (
                    <td
                      key={fieldIndex}
                      style={{
                        ...TABLE_STYLES.bodyCell,
                        width: field.width || "auto",
                      }}
                    >
                      {renderTableField(
                        field,
                        row,
                        sectionIndex,
                        rowIndex,
                        fieldIndex
                      )}
                    </td>
                  ))}
                  <td
                    style={{
                      ...TABLE_STYLES.bodyCell,
                      width: "70px",
                      textAlign: "center",
                    }}
                  >
                    <Button
                      size="small"
                      onClick={() =>
                        handleRemoveTableRow(sectionIndex, rowIndex)
                      }
                      sx={{ color: THEME.danger, fontSize: "12px" }}
                    >
                      Remove
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Button
            variant="outlined"
            size="small"
            onClick={() => handleAddTableRow(sectionIndex)}
            sx={{
              mt: 2,
              fontFamily: "DM Sans",
              fontSize: "12px",
              textTransform: "none",
            }}
          >
            + {section.addRowLabel || "Add Row"}
          </Button>
        </Box>
      );
    },
    [tableRows, renderTableField, handleAddTableRow, handleRemoveTableRow]
  );

  // ============================================
  // MEMOIZED STYLES
  // ============================================
  const containerStyles = useMemo(
    () => ({
      width: { xs: "100%", sm: "98%", md: "95%" },
      maxWidth: "1400px",
      margin: "0 auto",
      padding: { xs: 1.5, sm: 2.5, md: 3.5 },
      backgroundColor: "#fff",
      borderRadius: "12px",
      maxHeight: "80vh",
      overflowY: "auto",
      overflowX: "auto",
    }),
    []
  );

  // ============================================
  // RENDER
  // ============================================
  return (
    <>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 2,
          mb: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            alignItems: "center",
          }}
        >
          {showReportCountdownButton && <ReportCountdownButton />}
          {showSaveButton && <SaveButton onClick={handleSave}>Save</SaveButton>}
          <UserIdButton />
        </Box>
      </Box>

      {/* Title & Subtitle */}
      <Typography
        sx={{
          color: titleColor,
          fontWeight: "bold",
          fontFamily: "DM Sans",
          fontSize: "24px",
          mb: 2,
        }}
      >
        {title}
      </Typography>

      <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
        <Typography
          sx={{
            color: subtitleColor,
            fontWeight: "bold",
            fontFamily: "DM Sans",
            fontSize: "24px",
          }}
        >
          {subtitle}
        </Typography>
      </Box>

      {/* Form Container */}
      <Box sx={containerStyles}>
        <Typography
          sx={{
            fontSize: "18px",
            fontWeight: "bold",
            mb: 3,
            fontFamily: "DM Sans",
          }}
        >
          {formTitle}
        </Typography>

        {/* Sections */}
        {sections.map((section, sectionIndex) => (
          <Box key={sectionIndex} mb={4}>
            <Typography
              sx={{
                fontWeight: "bold",
                fontSize: "20px",
                fontFamily: "DM Sans",
                mb: 2,
              }}
            >
              {section.title}
            </Typography>

            {section.type === "table" ? (
              renderTable(section, sectionIndex)
            ) : (
              <>
                {section.description && (
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontFamily: "DM Sans",
                      mb: 2,
                      fontStyle: "italic",
                      color: THEME.darkGray,
                    }}
                  >
                    {section.description}
                  </Typography>
                )}
                {section.fields?.map((field, fieldIndex) => (
                  <Box
                    key={fieldIndex}
                    mb={field.type === "checkbox" ? 2 : 3}
                    sx={{
                      fontFamily: "DM Sans",
                      fontSize: "14px",
                      backgroundColor: field.disabled
                        ? THEME.lightGray
                        : "transparent",
                      padding: field.disabled ? "12px" : "0",
                      borderRadius: field.disabled ? "8px" : "0",
                      border: field.disabled
                        ? `1px solid ${THEME.border}`
                        : "none",
                    }}
                  >
                    <Typography
                      mb={1}
                      sx={{
                        fontWeight: field.disabled ? "bold" : "normal",
                        color: field.disabled ? "#333" : "inherit",
                      }}
                    >
                      {field.label}
                      {field.required && (
                        <span style={{ color: "red" }}> *</span>
                      )}
                    </Typography>
                    {renderField(field, sectionIndex, fieldIndex)}
                  </Box>
                ))}
              </>
            )}
          </Box>
        ))}

        {/* Add for Next Value button - inside form content */}
        {extraBottomActions?.length > 0 && (
          <Box sx={{ mt: 5, mb: 6, textAlign: "center" }}>
            {extraBottomActions.map((action, index) => {
              if (action.type === "addNext") {
                return (
                  <Button
                    key={index}
                    variant="outlined"
                    size="medium"
                    // onClick={handleAddAnother}
                    sx={{
                      borderColor: "#1976d2",
                      color: "#1976d2",
                      textTransform: "none",
                      fontFamily: "DM Sans",
                      fontWeight: 500,
                      px: 5,
                      py: 1,
                      borderRadius: "8px",
                    }}
                  >
                    Add for Next Value: Click here to Add
                  </Button>
                );
              }
              return null;
            })}
          </Box>
        )}
      </Box>

      {/* Action Buttons */}
      <Box
        sx={{
          mt: 4,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            width: { xs: "100%", sm: "70%", md: "50%" },
            maxWidth: "600px",
            alignItems: "center",
          }}
        >
          {showDownloadButton && (
            <DownloadButton
              title={downloadButtonTitle || "Download Template Form"}
              endIcon={<FaDownload style={{ fontSize: 20, marginLeft: 8 }} />}
              isDownloaded={isDownloaded}
              onClick={handleDownloadClick}
              sx={{
                width: "100% !important",
                maxWidth: "100% !important",
                minWidth: "100% !important",
                height: "64px",
                background: `linear-gradient(180deg, ${THEME.primary} 0%, ${THEME.secondary} 100%)`,
                color: "#fff",
                fontFamily: "DM Sans",
                fontSize: "20px",
                fontWeight: "700",
                borderRadius: "10px",
                textTransform: "none",
                "&:hover": {
                  background: `#023d28`,
                },
              }}
            />
          )}

          {showNextButton && (
            <Button
              variant="contained"
              onClick={handleNext}
              sx={{
                width: "100%",
                height: "64px",
                background: `linear-gradient(180deg, ${THEME.primary} 0%, ${THEME.secondary} 100%)`,
                color: "#fff",
                fontFamily: "DM Sans",
                fontSize: "20px",
                fontWeight: "700",
                borderRadius: "10px",
                textTransform: "none",
                "&:hover": {
                  background: `#023d28`,
                },
              }}
            >
              {nextButtonText}
            </Button>
          )}
        </Box>
      </Box>
    </>
  );
};

export default SmartTemplate;